import * as DropdownMenu from '@radix-ui/react-dropdown-menu';
import type { ReactNode } from 'react';
import { Check, MoreHorizontal } from 'lucide-react';
import { cn } from '@/lib/utils';
import { Button } from './button';

export const menuContentClass = cn(
  'z-[60] max-h-[var(--radix-dropdown-menu-content-available-height)] min-w-[13rem] overflow-y-auto rounded-xl border border-border/70 bg-popover/95 p-1.5 text-popover-foreground shadow-xl shadow-black/10 outline-none backdrop-blur no-print dark:shadow-black/40',
  'data-[state=open]:animate-in data-[state=open]:fade-in-0 data-[state=open]:zoom-in-95',
  'data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=closed]:zoom-out-95',
  'data-[side=bottom]:slide-in-from-top-1 data-[side=top]:slide-in-from-bottom-1',
);

export const menuItemClass = cn(
  'flex w-full cursor-default select-none items-center gap-2.5 rounded-md px-2.5 py-2 text-start text-sm outline-none transition-colors',
  'data-[highlighted]:bg-accent data-[highlighted]:text-accent-foreground',
  'data-[disabled]:cursor-not-allowed data-[disabled]:opacity-60',
  '[&>svg]:h-4 [&>svg]:w-4 [&>svg]:shrink-0 [&>svg]:text-muted-foreground data-[highlighted]:[&>svg]:text-accent-foreground',
);

export function ActionMenu({
  label,
  children,
  align = 'end',
  trigger,
  className,
}: {
  label: string;
  children: ReactNode;
  align?: 'start' | 'center' | 'end';
  trigger?: ReactNode;
  className?: string;
}) {
  return (
    <DropdownMenu.Root modal={false}>
      <DropdownMenu.Trigger asChild>
        {trigger ?? (
          <Button variant="ghost" size="icon" aria-label={label} onClick={(event) => event.stopPropagation()}>
            <MoreHorizontal className="h-4 w-4" />
          </Button>
        )}
      </DropdownMenu.Trigger>
      <DropdownMenu.Portal>
        <DropdownMenu.Content
          align={align}
          sideOffset={6}
          collisionPadding={8}
          onClick={(event) => event.stopPropagation()}
          className={cn(menuContentClass, className)}
        >
          {children}
        </DropdownMenu.Content>
      </DropdownMenu.Portal>
    </DropdownMenu.Root>
  );
}

export function ActionMenuItem({
  onSelect,
  icon,
  destructive,
  disabledReason,
  hint,
  selected,
  children,
}: {
  onSelect: () => void;
  icon?: ReactNode;
  destructive?: boolean;
  disabledReason?: string | null;
  hint?: ReactNode;
  selected?: boolean;
  children: ReactNode;
}) {
  const disabled = Boolean(disabledReason);
  return (
    <DropdownMenu.Item
      disabled={disabled}
      title={disabledReason ?? undefined}
      aria-checked={selected}
      onSelect={() => {
        if (!disabled) onSelect();
      }}
      className={cn(
        menuItemClass,
        selected && 'bg-primary/10 text-primary data-[highlighted]:bg-primary/15 data-[highlighted]:text-primary [&>svg]:text-primary',
        destructive &&
          !disabled &&
          'text-destructive data-[highlighted]:bg-destructive/10 data-[highlighted]:text-destructive [&>svg]:text-destructive data-[highlighted]:[&>svg]:text-destructive',
      )}
    >
      {icon}
      <span className="min-w-0 flex-1">
        <span className="block truncate">{children}</span>
        {disabledReason && <span className="block text-[11px] text-muted-foreground">{disabledReason}</span>}
        {!disabledReason && hint && <span className="block truncate text-[11px] text-muted-foreground">{hint}</span>}
      </span>
      {selected && <Check className="h-4 w-4 shrink-0" aria-hidden />}
    </DropdownMenu.Item>
  );
}

export function ActionMenuLabel({ children }: { children: ReactNode }) {
  return (
    <DropdownMenu.Label className="px-2.5 pb-1 pt-1.5 text-[11px] font-semibold uppercase tracking-wider text-muted-foreground">
      {children}
    </DropdownMenu.Label>
  );
}

export function ActionMenuSeparator() {
  return <DropdownMenu.Separator className="-mx-1.5 my-1.5 h-px bg-border" />;
}
